import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IDocumentResponse } from "../models/Document";
import {
  ICheckInItemsResponse,
  ICheckInStepsResponse,
  ICreateCheckInRequest,
  ICreateUpdateCheckInItemRequest,
  ICreateUpdateCheckInStepRequest,
  ISensitiveDocumentAccessResponse,
  ISingleCheckInItemResponse,
  ISingleCheckInResponse,
  ISingleCheckInStepResponse,
} from "../models/CheckIn";

/** Thrown when the API rejected the pin for a protected document, so the prompt can reopen. */
export class ProtectedDocumentPinError extends Error {}

export default function useCheckIn(vehicleId?: string, checkInId?: string, includeAllSteps = false) {
  const { get, getFile, post, postDocs, postRaw, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const checkIn = useQuery({
    queryKey: ["checkin", checkInId],
    queryFn: () => get<ISingleCheckInResponse>(`/checkin/${checkInId}`),
    enabled: !!checkInId,
    staleTime: 1000 * 60 * 2,
  });

  const checkInByVehicle = useQuery({
    queryKey: ["checkin-vehicle", vehicleId],
    queryFn: async () => {
      try {
        return await get<ISingleCheckInResponse>(`/checkin/vehicle/${vehicleId}`);
      } catch {
        return null;
      }
    },
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const checkInItems = useQuery({
    queryKey: ["checkin-items", checkInId],
    queryFn: () => get<ICheckInItemsResponse>(`/checkin/${checkInId}/items`),
    enabled: !!checkInId,
    staleTime: 1000 * 60,
  });

  const checkInSteps = useQuery({
    queryKey: ["checkin-steps"],
    queryFn: () => get<ICheckInStepsResponse>(`/checkin/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const checkInDocuments = useQuery({
    queryKey: ["checkin-documents", checkInId],
    queryFn: () => get<IDocumentResponse>(`/checkin/${checkInId}/documents`),
    enabled: !!checkInId,
    staleTime: 1000 * 60,
  });

  const allCheckInSteps = useQuery({
    queryKey: ["checkin-steps-all"],
    queryFn: () => get<ICheckInStepsResponse>(`/checkin/steps`),
    enabled: includeAllSteps,
    staleTime: 1000 * 60 * 10,
  });

  const createCheckIn = useMutation(
    (body: ICreateCheckInRequest) => post<ICreateCheckInRequest, ISingleCheckInResponse>("/checkin", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  const saveCheckInItem = useMutation(
    (body: ICreateUpdateCheckInItemRequest) => post<ICreateUpdateCheckInItemRequest, ISingleCheckInItemResponse>("/checkin/items", body),
    {
      onSuccess: (_, body) => {
        // A system step may have just written to the vehicle or its lead.
        queryClient.invalidateQueries(["system-step-values", body.vehicleId]);
        queryClient.invalidateQueries(["vehicles", body.vehicleId]);
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["checkin-items", checkInId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const createCheckInStep = useMutation(
    (body: ICreateUpdateCheckInStepRequest) => post<ICreateUpdateCheckInStepRequest, ISingleCheckInStepResponse>("/checkin/steps", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const updateCheckInStep = useMutation(
    (body: ICreateUpdateCheckInStepRequest) => put<ICreateUpdateCheckInStepRequest, ISingleCheckInStepResponse>(`/checkin/steps/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const updateCheckInStepOrder = useMutation(
    ({ checkInStepId, newIndex }: { checkInStepId: string, newIndex: number }) => put(`/checkin/steps/${checkInStepId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const deleteCheckInStep = useMutation(
    (checkInStepId: string) => del(`/checkin/steps/${checkInStepId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const uploadCheckInItemDocuments = useMutation(
    ({ checkInItemId, formData }: { checkInItemId: string, formData: FormData }) => postDocs(`/checkin/items/${checkInItemId}/documents`, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-documents", checkInId]);
        queryClient.invalidateQueries(["leadsDocuments"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  const downloadCheckInItemDocument = useMutation(
    ({ checkInItemId, documentId }: { checkInItemId: string, documentId: string }) => getFile(`/checkin/items/${checkInItemId}/documents/${documentId}`),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  /**
   * The only way to read a document on a check-in step marked sensitive — the plain download
   * endpoint 404s for those. Needs the `v5c` scope and the user's own pin, and the API records
   * every attempt.
   */
  const viewSensitiveDocument = useMutation(
    async ({ checkInItemId, documentId, pin }: { checkInItemId: string, documentId: string, pin: string }) => {
      // postRaw, not postGetFile: a rejected pin comes back 401 with a JSON body, and postGetFile
      // would hand that straight back as an image blob. The status code is what tells the caller to
      // reopen the pin prompt rather than show a broken image.
      const res = await postRaw(`/checkin/items/${checkInItemId}/documents/${documentId}/view`, { pin });

      if (res.status === 401) throw new ProtectedDocumentPinError("Incorrect pin");
      if (!res.ok) throw new Error(`Unable to open protected document (${res.status})`);

      return res.blob();
    },
    {
      onSuccess: () => {
        // A granted view adds an access-log row, so anything showing the log is now stale.
        queryClient.invalidateQueries(["checkin-document-access", checkInId]);
      },
    }
  );

  const sensitiveDocumentAccess = useQuery({
    queryKey: ["checkin-document-access", checkInId],
    queryFn: () => get<ISensitiveDocumentAccessResponse>(`/checkin/${checkInId}/documents/access`),
    // Opt-in: the endpoint needs the manage level of the v5c scope, so fetching it for everyone
    // would 403 on most users. Callers enable it once they know the user holds that level.
    enabled: false,
    staleTime: 1000 * 30,
    retry: false,
  });

  const deleteCheckInItemDocument = useMutation(
    ({ checkInItemId, documentId }: { checkInItemId: string, documentId: string }) => del(`/checkin/items/${checkInItemId}/documents/${documentId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-documents", checkInId]);
        queryClient.invalidateQueries(["leadsDocuments"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  return {
    checkIn,
    checkInByVehicle,
    checkInItems,
    checkInSteps,
    checkInDocuments,
    allCheckInSteps,
    createCheckIn,
    saveCheckInItem,
    createCheckInStep,
    updateCheckInStep,
    updateCheckInStepOrder,
    deleteCheckInStep,
    uploadCheckInItemDocuments,
    downloadCheckInItemDocument,
    viewSensitiveDocument,
    sensitiveDocumentAccess,
    deleteCheckInItemDocument,
  };
}
